import type { ReactElement } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import type { DeleteTargetType } from './api';
import { DeleteStatusBadge } from './DeleteStatusBadge';
import { useDeleteFlow } from './useDeleteFlow';

interface DeleteRecordButtonProps {
  readonly targetType: DeleteTargetType;
  readonly guid: string;
  /** Display name of the record — copy + type-to-confirm phrase. */
  readonly entityLabel: string;
  /** Fired on terminal success — navigate away here (the record is gone). */
  readonly onDeleted: () => void;
  /** Button label; defaults to "Delete". */
  readonly label?: string;
}

/**
 * Record-header delete affordance (family / patient). A `Trash2` button in the destructive tint that
 * runs the full preview → confirm/wizard → job flow; while the job runs it swaps to a "Deleting…"
 * badge, and on terminal success calls {@link DeleteRecordButtonProps.onDeleted} to navigate away.
 * Drop into an identity band's `actionSlot`.
 */
export function DeleteRecordButton({
  targetType,
  guid,
  entityLabel,
  onDeleted,
  label,
}: DeleteRecordButtonProps): ReactElement {
  const { t } = useTranslation();
  const del = useDeleteFlow({ targetType, guid, entityLabel, onDeleted });
  const text = label ?? t('delete.record.button', { defaultValue: 'Delete' });

  return (
    <>
      {del.phase !== null ? (
        <DeleteStatusBadge phase={del.phase} />
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1.5 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={del.start}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          {text}
        </Button>
      )}
      {del.overlay}
    </>
  );
}
